import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
  InputAdornment,
  TextField,
} from "@mui/material";
import {
  Visibility,
  CheckCircle,
  Cancel,
  FileDownload,
  DoneAll,
  Search,
} from "@mui/icons-material";
import useLayoutStore from "../../store/useLayoutStore";
import CustomTable from "../../components/common/CustomTable";
import CustomPaging from "../../components/common/CustomPaging";
import OrderDetailDialog from "../Order/OrderDetailDialog";
import http from "../../api/http";
import config from "../../config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getOrderById } from "../../api/order";

const Orders = () => {
  const theme = useTheme();
  const { isSidebarOpen } = useLayoutStore();

  // ✅ Breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.between("lg", "xl"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("xl"));

  // ✅ Sidebar width
  let drawerW = 0;
  if (isMobile) drawerW = 0;
  else if (isTablet) drawerW = 200;
  else if (isLaptop) drawerW = isSidebarOpen ? 240 : 80;
  else if (isDesktop) drawerW = isSidebarOpen ? 280 : 100;
  else if (isLargeScreen) drawerW = isSidebarOpen ? 320 : 120;

  // ✅ State
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(isMobile ? 10 : 15);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ✅ Dialog confirm (thêm reason + actionType)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    orderId: null,
    newStatus: null,
    message: "",
    actionType: "STATUS_UPDATE", // STATUS_UPDATE | ADMIN_REJECT | ADMIN_CANCEL
    reason: "",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await http.get(`/Order/admin-orders`, {
        params: {
          page,
          pageSize: itemsPerPage,
          search: searchQuery,
          statusId: statusFilter || undefined,
        },
      });
      setOrders(response.data.orders);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("❌ Lỗi khi lấy danh sách đơn hàng:", error);
      showSnackbar("Lỗi khi tải danh sách đơn hàng", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const response = await http.get(`/Order/order-statuses`);
      setStatusOptions(response.data);
    } catch (error) {
      console.error("❌ Lỗi khi lấy danh sách trạng thái:", error);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, itemsPerPage, searchQuery, statusFilter]);

  useEffect(() => {
    fetchStatusOptions();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter]);

  // ✅ OPEN confirm dialog
  const handleConfirmStatusChange = (orderId, newStatus, message) => {
    setConfirmDialog({
      open: true,
      orderId,
      newStatus,
      message,
      actionType: "STATUS_UPDATE",
      reason: "",
    });
  };

  // ✅ OPEN reject dialog (đi qua api revert)
  const handleConfirmReject = (orderId) => {
    setConfirmDialog({
      open: true,
      orderId,
      newStatus: 11, // reject statusId của bạn
      message:
        "Bạn có chắc muốn từ chối đơn hàng này? (Sẽ revert kho/voucher/điểm)",
      actionType: "ADMIN_REJECT",
      reason: "",
    });
  };

  // ✅ OPEN cancel dialog (đi qua api revert)
  const handleConfirmCancel = (orderId) => {
    setConfirmDialog({
      open: true,
      orderId,
      newStatus: 10, // cancel statusId của bạn (nếu khác thì sửa lại)
      message:
        "Bạn có chắc muốn huỷ đơn hàng này? (Sẽ revert kho/voucher/điểm)",
      actionType: "ADMIN_CANCEL",
      reason: "",
    });
  };

  const handleViewOrderDetail = async (orderId) => {
    try {
      const order = await getOrderById(orderId);
      setSelectedOrder(order);
    } catch (error) {
      showSnackbar("Không thể tải chi tiết đơn hàng", "error");
    }
  };

  // ✅ Submit confirm
  const handleStatusUpdateConfirmed = async () => {
    const { orderId, newStatus, actionType, reason } = confirmDialog;

    // Validate reason cho cancel/reject
    if (
      (actionType === "ADMIN_REJECT" || actionType === "ADMIN_CANCEL") &&
      (!reason || !reason.trim())
    ) {
      showSnackbar("Vui lòng nhập lý do để huỷ/từ chối.", "warning");
      return;
    }

    try {
      if (actionType === "ADMIN_REJECT") {
        // ✅ API riêng cho reject (có revert)
        await http.put(`/Order/admin-reject/${orderId}`, { reason: reason.trim() });
        showSnackbar("Từ chối đơn hàng thành công (đã revert).");
      } else if (actionType === "ADMIN_CANCEL") {
        // ✅ API riêng cho cancel (có revert)
        await http.put(`/Order/admin-cancel/${orderId}`, {
          reason: reason.trim(),
        });
        showSnackbar("Huỷ đơn hàng thành công (đã revert).");
      } else {
        // ✅ Update status bình thường (KHÔNG dùng cho cancel/reject)
        await http.put(`/Order/UpdateStatus`, { orderId, status: newStatus });
        showSnackbar("Cập nhật trạng thái thành công");
      }

      fetchOrders();
    } catch (error) {
      console.error("❌ Không thể cập nhật trạng thái:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Không thể cập nhật trạng thái đơn hàng";
      showSnackbar(msg, "error");
    } finally {
      setConfirmDialog({
        open: false,
        orderId: null,
        newStatus: null,
        message: "",
        actionType: "STATUS_UPDATE",
        reason: "",
      });
    }
  };

  const exportToExcel = () => {
    const exportData = orders.map((o) => ({
      "Mã đơn": o.orderId,
      "Người nhận": o.receiverName,
      Email: o.email,
      "Tổng tiền": o.totalPrice,
      "Trạng thái": o.statusName,
      "Ngày đặt": new Date(o.orderDate).toLocaleString("vi-VN"),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "DanhSachDonHang.xlsx");
    showSnackbar("Xuất Excel thành công");
  };

  const getShortStatusName = (statusName) => {
    const statusMap = {
      "Chờ xác nhận": "Chờ XN",
      "Đã xác nhận": "Đã XN",
      "Đang xử lý": "Đang XL",
      "Đã giao hàng": "Đã GH",
      "Đã hủy": "Đã Hủy",
      "Từ chối": "Từ chối",
      "Hoàn thành": "Hoàn tất",
      "Trả hàng": "Trả hàng",
    };
    return statusMap[statusName] || statusName.substring(0, 6);
  };

  // ✅ helper: có thể show nút huỷ ở các trạng thái nào
  const canAdminCancel = (statusId) => {
    // tuỳ rule của bạn, ví dụ: chưa hoàn thành/đã huỷ/đã từ chối
    return ![10, 11, 12].includes(statusId);
  };

  const orderColumns = [
    {
      field: "orderId",
      label: "Mã đơn",
      minWidth: isMobile ? "80px" : "120px",
      hideOnMobile: false,
    },
    {
      field: "receiverName",
      label: "Người nhận",
      minWidth: isMobile ? "120px" : "180px",
      hideOnMobile: false,
    },
    {
      field: "totalPrice",
      label: "Tổng tiền",
      minWidth: isMobile ? "100px" : "120px",
      customRender: (row) => `${row.totalPrice.toLocaleString()}₫`,
      hideOnMobile: true,
    },
    {
      field: "statusName",
      label: "Trạng thái",
      minWidth: isMobile ? "100px" : "120px",
      customRender: (row) => (
        <Chip
          label={isMobile ? getShortStatusName(row.statusName) : row.statusName}
          color={
            row.statusId === 6
              ? "warning"
              : row.statusId === 7
                ? "info"
                : row.statusId === 8
                  ? "primary"
                  : row.statusId === 9
                    ? "success"
                    : row.statusId === 10
                      ? "error"
                      : row.statusId === 11
                        ? "error"
                        : row.statusId === 12
                          ? "success"
                          : row.statusId === 13
                            ? "secondary"
                            : "default"
          }
          size="small"
          variant="outlined"
          sx={{ fontSize: isMobile ? "0.7rem" : "0.8rem" }}
        />
      ),
      hideOnMobile: false,
    },
    {
      field: "orderDate",
      label: "Ngày đặt",
      minWidth: isMobile ? "100px" : "150px",
      customRender: (row) =>
        isMobile
          ? new Date(row.orderDate).toLocaleDateString("vi-VN")
          : new Date(row.orderDate).toLocaleString("vi-VN"),
      hideOnMobile: true,
    },
    {
      field: "actions",
      label: "Thao tác",
      minWidth: isMobile ? "160px" : "280px",
      customRender: (row) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <Tooltip title="Xem chi tiết">
            <IconButton
              color="primary"
              onClick={() => handleViewOrderDetail(row.orderId)}
              size="small"
            >
              <Visibility fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>

          {/* ✅ Pending (6): confirm / reject / cancel */}
          {row.statusId === 6 && (
            <>
              <Tooltip title="Xác nhận đơn">
                <IconButton
                  color="success"
                  onClick={() =>
                    handleConfirmStatusChange(
                      row.orderId,
                      7,
                      "Bạn có chắc muốn xác nhận đơn hàng này?",
                    )
                  }
                  size="small"
                >
                  <CheckCircle fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Từ chối đơn (revert)">
                <IconButton
                  color="warning"
                  onClick={() => handleConfirmReject(row.orderId)}
                  size="small"
                >
                  <Cancel fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Huỷ đơn (revert)">
                <IconButton
                  color="error"
                  onClick={() => handleConfirmCancel(row.orderId)}
                  size="small"
                >
                  <Cancel fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* ✅ Confirmed (7): to processing / reject / cancel */}
          {row.statusId === 7 && (
            <>
              <Tooltip title="Chuyển sang đang xử lý">
                <IconButton
                  color="info"
                  onClick={() =>
                    handleConfirmStatusChange(
                      row.orderId,
                      8,
                      "Chuyển đơn sang trạng thái đang xử lý?",
                    )
                  }
                  size="small"
                >
                  <CheckCircle fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Từ chối đơn (revert)">
                <IconButton
                  color="warning"
                  onClick={() => handleConfirmReject(row.orderId)}
                  size="small"
                >
                  <Cancel fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Huỷ đơn (revert)">
                <IconButton
                  color="error"
                  onClick={() => handleConfirmCancel(row.orderId)}
                  size="small"
                >
                  <Cancel fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* ✅ Processing (8): delivered / cancel (tuỳ rule) */}
          {row.statusId === 8 && (
            <>
              <Tooltip title="Đánh dấu đã giao hàng">
                <IconButton
                  color="secondary"
                  onClick={() =>
                    handleConfirmStatusChange(
                      row.orderId,
                      9,
                      "Đánh dấu đơn đã giao hàng?",
                    )
                  }
                  size="small"
                >
                  <CheckCircle fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>

              {canAdminCancel(row.statusId) && (
                <Tooltip title="Huỷ đơn (revert)">
                  <IconButton
                    color="error"
                    onClick={() => handleConfirmCancel(row.orderId)}
                    size="small"
                  >
                    <Cancel fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}

          {/* ✅ Delivered (9): complete */}
          {row.statusId === 9 && (
            <Tooltip title="Hoàn thành đơn hàng">
              <IconButton
                color="success"
                onClick={() =>
                  handleConfirmStatusChange(
                    row.orderId,
                    12,
                    "Hoàn thành đơn hàng này?",
                  )
                }
                size="small"
              >
                <DoneAll fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
      hideOnMobile: false,
    },
  ];

  const isNeedReason =
    confirmDialog.actionType === "ADMIN_REJECT" ||
    confirmDialog.actionType === "ADMIN_CANCEL";

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 1, sm: 2 },
        width: isMobile || isTablet ? "100vw" : `calc(100vw - ${drawerW}px)`,
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
            Danh sách đơn hàng
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Đang hiển thị {(page - 1) * itemsPerPage + 1}–
            {Math.min(page * itemsPerPage, totalItems)} trong tổng số{" "}
            {totalItems} đơn hàng
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={exportToExcel}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: "auto" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {isMobile ? "Excel" : "Xuất Excel"}
        </Button>
      </Box>

      {/* Search + Filter */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Tìm kiếm đơn hàng..."
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 250, md: 300 },
              width: { xs: "100%", sm: "auto" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl
            sx={{
              minWidth: { xs: "100%", sm: 180, md: 200 },
              width: { xs: "100%", sm: "auto" },
            }}
            size="small"
          >
            <InputLabel>Trạng thái</InputLabel>
            <Select
              value={statusFilter}
              label="Trạng thái"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">Tất cả</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status.statusId} value={status.statusId}>
                  {status.statusName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>

      {/* Table / Loading */}
      {loading ? (
        <CircularProgress sx={{ alignSelf: "center", mt: 5 }} />
      ) : (
        <>
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <CustomTable
              columns={orderColumns}
              data={orders}
              isMobile={isMobile}
            />
          </Box>

          <CustomPaging
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            totalItems={totalItems}
            isMobile={isMobile}
          />
        </>
      )}

      {/* Empty state */}
      {!loading && orders.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Typography variant="body1" align="center">
            {searchQuery || statusFilter
              ? "Không có đơn hàng nào phù hợp với điều kiện tìm kiếm"
              : "Chưa có đơn hàng nào"}
          </Typography>
        </Box>
      )}

      {/* Order Detail Dialog */}
      {selectedOrder && (
        <OrderDetailDialog
          open={Boolean(selectedOrder)}
          onClose={() => setSelectedOrder(null)}
          order={selectedOrder}
          fullScreen={isMobile}
        />
      )}

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({
            open: false,
            orderId: null,
            newStatus: null,
            message: "",
            actionType: "STATUS_UPDATE",
            reason: "",
          })
        }
        PaperProps={{
          sx: {
            backgroundColor: (theme) => theme.palette.background.paper,
            color: (theme) => theme.palette.text.primary,
            borderRadius: 2,
            p: 2,
            m: { xs: 1, sm: 2 },
            maxWidth: { xs: "95%", sm: "520px" },
          },
        }}
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: "bold", p: { xs: 1, sm: 2 } }}>
          Xác nhận thao tác
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
          <DialogContentText sx={{ color: "text.primary", mb: 2 }}>
            {confirmDialog.message}
          </DialogContentText>

          {isNeedReason && (
            <TextField
              value={confirmDialog.reason}
              onChange={(e) =>
                setConfirmDialog((prev) => ({
                  ...prev,
                  reason: e.target.value,
                }))
              }
              placeholder="Nhập lý do..."
              label="Lý do"
              fullWidth
              multiline
              minRows={2}
              size="small"
            />
          )}
        </DialogContent>

        <DialogActions sx={{ p: { xs: 1, sm: 2 } }}>
          <Button
            onClick={() =>
              setConfirmDialog({
                open: false,
                orderId: null,
                newStatus: null,
                message: "",
                actionType: "STATUS_UPDATE",
                reason: "",
              })
            }
            color="inherit"
            size={isMobile ? "small" : "medium"}
          >
            Hủy
          </Button>
          <Button
            onClick={handleStatusUpdateConfirmed}
            color="primary"
            autoFocus
            size={isMobile ? "small" : "medium"}
          >
            Xác nhận
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{
          vertical: isMobile ? "bottom" : "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", fontSize: isMobile ? "0.875rem" : "1rem" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Orders;
